import {
  FormEvent,
  FormEventHandler,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

export type FormValues = Record<PropertyKey, unknown>;

export interface UseFormConfiguration<T extends FormValues> {
  /** Initial values to be used and that will be compared to know if something has changed */
  initialValues: T;
  /**
   * Function that will be triggered when the <form> using the "handleOnSubmit" returned handler is submitted and
   * all the validations have passed.
   */
  onSubmit: (values: T, event?: FormEvent) => void;
  /**
   * Optional validation function. It provides an utility function to set error values to any of the fields in "values"
   * object   */
  validate?: (
    values: T,
    setError: (key: keyof T, message: string) => void,
  ) => void;
  /**
   * Object that includes function to check if a value is different than other for specific fields.
   * By default, reference inequality is used to check if values are different (!==), so optional checkers can be
   * provided if complex types are used (object, arrays, etc).
   *
   * IMPORTANT: Be careful to type properly the values to compare, as the typechecking cannot enforce the type of the
   * values object at this point, so this is open to errors in execution time if a conflicting type or check is used,  */
  differenceChecker?: Partial<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<keyof T, (a: any, b: any) => boolean>
  >;
  /**
   * Optional value to indicate if the form should be reset if the referential equality of the "initialValues" field
   * changes from one execution to the next.
   *
   * Useful in cases where the "initialValues" instance is strongly controlled and only changes when a form should
   * be handling new values from the beginning.
   *
   * If the value is `true`, the form will reset every time the instance provided changes. If instead a function
   * is provided, its returned value will be checked before resetting the form.
   */
  resetOnInitialValuesChange?: boolean | (() => boolean);
}

export interface UseFormResult<T extends FormValues> {
  /** Current values of the state values */
  values: T;
  /** Main function to update the state values */
  updateValues: (values: Partial<T>, touch?: boolean) => void;
  /** Utility to dynamically add new fields to the values object */
  addFields: (fields: Partial<T>) => void;
  /** Utility to dynamically remove fields to the values object */
  removeFields: (...fields: Array<keyof T>) => void;
  /** Object with boolean values indicating if a field has been touched by the user, meaning that validation errors should be displayed */
  touched: Record<keyof T, boolean>;
  /** Utility to imperatively indicate one or more fields have been touched */
  touchField: (...fieldNames: Array<keyof T>) => void;
  /** Submit handler to be passed to the wrapping <form> element. It just takes care of inhibiting the "submit" DOM event and
   * will check the validity of the form values before triggering the "onSubmit" callback provided to the hook, ensuring that
   * that function will be called always if the validations have been passed. It will also
   * mark all the fields as "touched".
   */
  handleSubmit: FormEventHandler;
  /**
   * Value that indicates if any field has a different value than the initial values passed to the hook.
   */
  haveValuesChanged: boolean;
  /**
   * Object with the visible errors detected for the different fields of the current values object. It will only have
   * the fields that have failed their validation and are also touched.
   */
  errors: Partial<Record<keyof T, string>>;
  /**
   * Utility value that indicates if there is at least one key and value in the "errors" object, meaning there is at least one visible error.
   */
  hasVisibleErrors: boolean;
  /**
   * Object with all errors detected for the different fields for the current values object. It will have all fields that have
   * failed theur validation even if they aren't touched.
   */
  allErrors: Partial<Record<keyof T, string>>;
  /**
   * Utility value that indicates if there is at least one key and value in the "allErrors" object, meaning that at least one
   * field has failed the validation, not caring if the error is being displayed or not.
   */
  isValid: boolean;
  /**
   * Utility function to imperatively reset the form to the current "initialValues" object passed to the hook. It also resets the
   * "touched" and "errors" related objects and variables.
   */
  reset: () => void;
  /** Manually request the form submission, the event will be undefined in this case. It will still only trigger the "onSubmit" callback if the validation conditions are met. */
  requestSubmit: () => void;
}

interface UseFormReducerState<T extends FormValues> {
  values: T;
  touched: Record<keyof T, boolean>;
}

interface UpdateValuesAction<T extends FormValues> {
  type: 'UpdateValues';
  payload: { values: Partial<T>; touch: boolean };
}

interface AddFieldsAction<T extends FormValues> {
  type: 'AddFields';
  payload: { fields: Partial<T> };
}

interface RemoveFieldsAction<T extends FormValues> {
  type: 'RemoveFields';
  payload: { fields: Array<keyof T> };
}

interface TouchFieldsAction<T extends FormValues> {
  type: 'TouchFields';
  payload: Array<keyof T>;
}

interface ResetFormAction<T extends FormValues> {
  type: 'ResetForm';
  payload: T;
}

type UseFormReducerAction<T extends FormValues> =
  | UpdateValuesAction<T>
  | AddFieldsAction<T>
  | RemoveFieldsAction<T>
  | TouchFieldsAction<T>
  | ResetFormAction<T>;

const reducer = <T extends FormValues>(
  prev: UseFormReducerState<T>,
  action: UseFormReducerAction<T>,
): UseFormReducerState<T> => {
  let newState = prev;
  switch (action.type) {
    case 'UpdateValues':
      {
        newState = {
          values: { ...prev.values, ...action.payload.values },
          touched: !action.payload.touch
            ? prev.touched
            : Object.keys(action.payload.values).reduce(
                (acc, key) => ({
                  ...acc,
                  [key]: true,
                }),
                prev.touched,
              ),
        };
      }
      break;
    case 'AddFields':
      {
        const newFieldKeys = Object.keys(action.payload.fields).filter(
          key => typeof prev.values[key] === 'undefined',
        );
        if (newFieldKeys.length > 0) {
          newState = {
            values: newFieldKeys.reduce(
              (acc, newKey) => ({
                ...acc,
                [newKey]: action.payload.fields[newKey],
              }),
              prev.values,
            ),
            touched: newFieldKeys.reduce(
              (acc, newKey) => ({
                ...acc,
                [newKey]: false,
              }),
              prev.touched,
            ),
          };
        }
      }
      break;
    case 'RemoveFields':
      {
        const removedFieldKeys = action.payload.fields.filter(
          key => typeof prev.values[key] !== 'undefined',
        );
        if (removedFieldKeys.length > 0) {
          const newValues = { ...prev.values };
          const newTouched = { ...prev.touched };

          removedFieldKeys.forEach(key => {
            delete newValues[key];
            delete newTouched[key];
          });

          newState = {
            values: newValues,
            touched: newTouched,
          };
        }
      }
      break;
    case 'TouchFields':
      {
        const keys =
          action.payload.length > 0 ? action.payload : Object.keys(prev.values);
        if (keys.some(key => !prev.touched[key])) {
          newState = {
            ...prev,
            touched: keys.reduce(
              (acc, key) => ({
                ...acc,
                [key]: true,
              }),
              prev.touched,
            ),
          };
        }
      }
      break;
    case 'ResetForm':
      {
        newState = {
          values: action.payload,
          touched: Object.keys(action.payload).reduce(
            (acc, key) => ({ ...acc, [key]: false }),
            {} as Record<keyof T, boolean>,
          ),
        };
      }
      break;
    default:
  }
  return newState;
};

/**
 * Generic hook to handle a form state.
 */
const useForm = <T extends FormValues>({
  initialValues,
  onSubmit,
  validate,
  differenceChecker,
  resetOnInitialValuesChange = false,
}: UseFormConfiguration<T>): UseFormResult<T> => {
  const changingValuesRef = useRef({
    onSubmit,
    validate,
    initialValues,
    differenceChecker,
  });
  changingValuesRef.current.onSubmit = onSubmit;
  changingValuesRef.current.validate = validate;
  changingValuesRef.current.differenceChecker = differenceChecker;
  changingValuesRef.current.initialValues = initialValues;

  const [reducerState, dispatch] = useReducer<typeof reducer<T>, T>(
    reducer,
    initialValues,
    values => ({
      values,
      touched: Object.keys(initialValues).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {},
      ) as Record<keyof T, boolean>,
    }),
  );

  const { updateValues, addFields, removeFields, touchField, reset } =
    useMemo(() => {
      return {
        updateValues: (updatedValues: Partial<T>, touch = false) => {
          dispatch({
            type: 'UpdateValues',
            payload: { values: updatedValues, touch },
          });
        },
        addFields: (fields: Partial<T>) => {
          dispatch({
            type: 'AddFields',
            payload: { fields },
          });
        },
        removeFields: (...fields: Array<keyof T>) => {
          dispatch({
            type: 'RemoveFields',
            payload: { fields },
          });
        },
        touchField: (...fields: Array<keyof T>) => {
          dispatch({
            type: 'TouchFields',
            payload: fields,
          });
        },
        reset: () => {
          dispatch({
            type: 'ResetForm',
            payload: changingValuesRef.current.initialValues,
          });
        },
      };
    }, []);

  const {
    allErrors,
    errors,
    haveValuesChanged,
    handleSubmit,
    isValid,
    hasVisibleErrors,
    submit,
  } = useMemo(() => {
    const { initialValues, validate, differenceChecker } =
      changingValuesRef.current;

    let isValid = true;

    const errors: Partial<Record<keyof T, string>> = {};

    validate?.(reducerState.values, (key, message) => {
      message = message.trim();
      if (message) {
        isValid = false;
        errors[key] = message;
      }
    });

    const visibleErrors: typeof errors = Object.keys(errors).reduce(
      (acc, key) =>
        reducerState.touched[key] ? { ...acc, [key]: errors[key] } : acc,
      {} as Partial<Record<keyof T, string>>,
    );

    const hasVisibleErrors = Object.keys(visibleErrors).length > 0;

    const currentValuesKeys = Object.keys(reducerState.values);

    const submit = (event?: FormEvent<Element>) => {
      const isValid = Object.values(errors).filter(Boolean).length === 0;
      if (isValid) {
        changingValuesRef.current.onSubmit(reducerState.values, event);
      }
      dispatch({ type: 'TouchFields', payload: [] });
    };

    return {
      haveValuesChanged:
        Object.keys(initialValues).length !== currentValuesKeys.length ||
        (currentValuesKeys.length > 0 &&
          currentValuesKeys.some(key =>
            differenceChecker?.[key]
              ? differenceChecker[key](
                  reducerState.values[key],
                  initialValues[key],
                )
              : reducerState.values[key] !== initialValues[key],
          )),
      allErrors: errors,
      errors: visibleErrors,
      hasVisibleErrors,
      isValid,
      handleSubmit: (event: FormEvent<Element>) => {
        event.preventDefault();
        submit(event);
      },
      submit,
    };
  }, [reducerState]);

  useEffect(() => {
    if (!resetOnInitialValuesChange) {
      return;
    }

    if (
      typeof resetOnInitialValuesChange === 'boolean' ||
      resetOnInitialValuesChange()
    ) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return {
    allErrors,
    errors,
    handleSubmit,
    hasVisibleErrors,
    haveValuesChanged,
    isValid,
    reset,
    touched: reducerState.touched,
    touchField,
    updateValues,
    addFields,
    removeFields,
    values: reducerState.values,
    requestSubmit: submit,
  };
};

export default useForm;
